import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { isClientRole } from '../auth/roleUtils';
import { useSubscription } from '../hooks/useSubscription';

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children, enabled = true }) {
    const { user, isAuthenticated } = useAuth();
    const [paywallStatus, setPaywallStatus] = useState(null);
    const role = user?.role || user?.userRole || localStorage.getItem('userRole');
    const isClientUser = isClientRole(role);
    const subscriptionEnabled = enabled && isAuthenticated && !!user && !isClientUser;
    const subscriptionScope =
        user?.organizationId ||
        user?.OrganizationId ||
        user?.organizationReferenceId ||
        user?.OrganizationReferenceId ||
        user?.sub ||
        user?.referenceId ||
        localStorage.getItem('userId') ||
        'current';

    const {
        data: subscription,
        isLoading: loading,
        error,
        refetch
    } = useSubscription({
        scope: subscriptionScope,
        enabled: subscriptionEnabled,
    });

    const show402Paywall = useCallback((status) => {
        setPaywallStatus(status);
    }, []);

    const dismissPaywall = useCallback(() => {
        setPaywallStatus(null);
    }, []);

    useEffect(() => {
        if (!subscriptionEnabled) {
            setPaywallStatus(null);
            return undefined;
        }

        const handler = (e) => {
            const status = e.detail?.status || 'None';
            show402Paywall(status);
        };
        window.addEventListener('vela:subscription-required', handler);
        return () => window.removeEventListener('vela:subscription-required', handler);
    }, [subscriptionEnabled, show402Paywall]);

    return (
        <SubscriptionContext.Provider value={{
            subscription: subscriptionEnabled ? subscription || null : null,
            loading: subscriptionEnabled && loading,
            error: subscriptionEnabled ? error : null,
            refetch,
            paywallStatus,
            show402Paywall,
            dismissPaywall
        }}>
            {children}
        </SubscriptionContext.Provider>
    );
}

export function useSubscriptionContext() {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
    }
    return context;
}
